import {
    BrainCircuit,
    Activity,
    Eye,
    ShieldAlert,
    ArrowRight,
    Lock,
    Terminal,
} from 'lucide-react';
import { useAuth } from '../context/AuthProvider';
import { motion } from 'framer-motion';

const SignIn = () => {
    const { keycloak } = useAuth();

    const handleSignIn = () => {
        keycloak.login({ redirectUri: window.location.origin + '/dashboard' });
    };

    const securityFeatures = [
        {
            icon: <Eye className="w-5 h-5" />,
            title: "Live Stream Aggregation",
            description: "Ultra-low latency viewing of distributed IP cameras.",
        },
        {
            icon: <BrainCircuit className="w-5 h-5" />,
            title: "AI Video Analytics",
            description: "Instant perimeter breach and object detection.",
        },
        {
            icon: <Activity className="w-5 h-5" />,
            title: "Asset Health",
            description: "Automated network and storage diagnostics.",
        },
        {
            icon: <ShieldAlert className="w-5 h-5" />,
            title: "Proactive Alerts",
            description: "Real-time intelligent alert routing.",
        },
    ];

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row overflow-hidden font-sans selection:bg-[#1A1A2E] selection:text-white relative">
            {/* Tech Grid Background (Full Page Overlay) */}
            <div
                className="absolute inset-0 opacity-[0.05] pointer-events-none z-0"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 40L40 0L0 0L0 40ZM39 39L1 39L1 1L39 1L39 39Z'/%3E%3C/g%3E%3C/svg%3E")`,
                }}
            />

            {/* --- LEFT SIDE: THE COMMAND CENTER VISUAL --- */}
            <div className="relative flex-1 hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-slate-200 bg-white/50 backdrop-blur-sm">
                {/* Ambient Glows */}
                <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#1A1A2E]/10 blur-[120px] rounded-full" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-slate-400/10 blur-[100px] rounded-full" />

                <div className="relative z-10">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-3 bg-white w-fit px-4 py-2 rounded-full border border-slate-200 shadow-sm"
                    >
                        <Terminal className="w-4 h-4 text-[#1A1A2E]" />
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
                            All Systems Online
                        </span>
                    </motion.div>
                </div>

                <div className="relative z-10 flex flex-col items-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="relative w-full max-w-2xl"
                    >
                        <div className="absolute -inset-4 border border-slate-100 rounded-2xl pointer-events-none" />
                        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#1A1A2E]/20 rounded-tl-lg" />
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#1A1A2E]/20 rounded-br-lg" />

                        <img
                            src="/cam_monitor.png"
                            alt="Dashboard Preview"
                            className="w-full h-auto rounded-xl shadow-2xl shadow-slate-200 relative z-10"
                        />

                        <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -bottom-6 -left-6 bg-white border border-slate-200 p-4 rounded-xl shadow-2xl z-20 hidden xl:block"
                        >
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest">
                                    Active nodes
                                </span>
                            </div>
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <div key={i} className="w-1.5 h-6 bg-slate-200 rounded-full overflow-hidden">
                                        <motion.div
                                            animate={{ height: ["20%", `${Math.random() * 80 + 20}%`, "20%"] }}
                                            transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                                            className="w-full bg-[#1A1A2E]"
                                        />
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                <div className="relative z-10">
                    <h2 className="text-4xl font-black text-slate-900 leading-tight mb-4">
                        Smart <br />
                        <span className="text-[#1A1A2E]">Camera Monitoring</span>
                    </h2>
                    <div className="grid grid-cols-2 gap-6 max-w-xl">
                        {securityFeatures.slice(0, 2).map((f, i) => (
                            <div key={i} className="space-y-1">
                                <div className="flex items-center gap-2 text-[#1A1A2E]">
                                    {f.icon}
                                    <span className="text-xs font-black uppercase tracking-widest">{f.title}</span>
                                </div>
                                <p className="text-slate-500 text-[11px] font-medium leading-relaxed">
                                    {f.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* --- RIGHT SIDE: LOGIN PORTAL --- */}
            <div className="flex-1 flex flex-col justify-center items-center p-8 bg-slate-50/50 relative">
                <div className="lg:hidden absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-[#1A1A2E]/10 blur-[100px] rounded-full pointer-events-none" />

                <div className="w-full max-w-[400px] relative z-20">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <motion.div
                            whileHover={{ scale: 1.05, rotate: 5 }}
                            className="inline-block relative mb-1"
                        >
                            <div className="absolute inset-0 bg-[#1A1A2E]/10 blur-2xl rounded-full" />
                            <img src="/AP_Police_logo.png" alt="Logo" className="w-24 h-24 relative z-10" />
                        </motion.div>

                        <h1 className="text-3xl font-black text-slate-900 tracking-tighter mb-1">
                            OVUL<span className="text-[#1A1A2E]">SCANNER</span>
                        </h1>
                        <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.25em]">
                            Camera Monitoring System
                        </p>
                    </div>

                    {/* Action Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white border border-slate-200 rounded-3xl p-8 shadow-2xl relative group overflow-hidden"
                    >
                        <div className="absolute inset-0 w-full h-full bg-gradient-to-tr from-transparent via-slate-50 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />

                        <div className="flex items-center gap-4 mb-8 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <div className="p-3 bg-[#1A1A2E] rounded-xl">
                                <Lock className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="text-slate-900 font-black text-sm uppercase tracking-wide">
                                    Secure Login
                                </h3>
                                <p className="text-slate-500 text-[10px] font-bold">Authorized Access</p>
                            </div>
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleSignIn}
                            className="group relative w-full h-14 bg-[#1A1A2E] rounded-2xl overflow-hidden shadow-xl shadow-[#1A1A2E]/20"
                        >
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                            <div className="relative z-10 flex items-center justify-center gap-3">
                                <span className="text-white font-black text-sm uppercase tracking-widest">Sign In</span>
                                <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform" />
                            </div>
                        </motion.button>

                        {/* <div className="mt-8 pt-8 border-t border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Protocol V2.10</span>
                            </div>
                            <div className="flex items-center gap-2 opacity-60">
                                <div className="w-1.5 h-1.5 rounded-full bg-[#1A1A2E]" />
                                <span className="text-[9px] font-black text-slate-900 uppercase tracking-widest">Encrypted</span>
                            </div>
                        </div> */}
                    </motion.div>

                    {/* Footer Info */}
                    <div className="mt-12 flex justify-center gap-8">
                        <div className="text-center">
                            <div className="text-slate-900 font-black text-sm">99.9%</div>
                            <div className="text-slate-400 text-[8px] font-black uppercase tracking-tighter">Uptime</div>
                        </div>
                        <div className="w-px h-8 bg-slate-200" />
                        <div className="text-center">
                            <div className="text-slate-900 font-black text-sm">256-BIT</div>
                            <div className="text-slate-400 text-[8px] font-black uppercase tracking-tighter">Security</div>
                        </div>
                        <div className="w-px h-8 bg-slate-200" />
                        <div className="text-center">
                            <div className="text-slate-900 font-black text-sm">REALTIME</div>
                            <div className="text-slate-400 text-[8px] font-black uppercase tracking-tighter">Monitoring</div>
                        </div>
                    </div>

                    {/* Footer / Copyright */}
                    {/* <div className="mt-16 text-center">
                        <p className="text-[9px] font-black text-black uppercase tracking-widest leading-relaxed">
                            © 2026 OvulScanner <br />
                            <span className="text-black">Powered by</span>{' '}
                            <span className="text-black">Runners Software Technology</span>
                        </p>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default SignIn;